import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/ErrorState'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { ReservationBoardTable } from '@/components/venue/ReservationBoardTable'
import { ReservationCalendarGrid } from '@/components/venue/ReservationCalendarGrid'
import { ReservationCourtSummaryPanel } from '@/components/venue/ReservationCourtSummaryPanel'
import { ReservationLegend } from '@/components/venue/ReservationLegend'
import { ReservationManualCreateModal } from '@/components/venue/ReservationManualCreateModal'
import { useCourts } from '@/hooks/useCourts'
import { useReservationAvailability } from '@/hooks/useReservationAvailability'
import { useVenueReservationBoard } from '@/hooks/useVenueReservationBoard'
import { useVenueReservationCalendar } from '@/hooks/useVenueReservationCalendar'
import { buildMonthGrid, buildWeekGrid, addDays, addMonths, todayDateKey } from '@/lib/venue/calendarGrid'
import { useVenueContext } from '@/routes/venueContext'

type ViewMode = 'MONTH' | 'WEEK'

const CLOSED_NOTICE = '이 날짜는 휴무로 설정되어 있어 예약을 받을 수 없습니다.'
const NOT_OPERATING_NOTICE = '이 날짜는 운영시간이 설정되지 않아 예약을 받을 수 없습니다.'

/**
 * 예약 캘린더(설계 §2-4(1)) — 월간·주간 보기 전환, 이전/다음 기간 이동, 날짜 선택, 코트별 시간대
 * 현황(현황판) 렌더. 신규 백엔드 EP 없음 — #666 이 구현한 (a) 캘린더 요약 · (b) 현황판 EP 를 소비한다.
 */
export function VenueBookingsCalendarPage() {
  const { venueId } = useVenueContext()
  const navigate = useNavigate()

  const [viewMode, setViewMode] = useState<ViewMode>('MONTH')
  const [referenceDate, setReferenceDate] = useState(todayDateKey)
  const [selectedDate, setSelectedDate] = useState(todayDateKey)
  const [courtId, setCourtId] = useState<number | 'ALL'>('ALL')
  const [manualCreateOpen, setManualCreateOpen] = useState(false)

  const courtsQuery = useCourts(venueId)
  const availability = useReservationAvailability(venueId)

  const cells = useMemo(
    () => (viewMode === 'MONTH' ? buildMonthGrid(referenceDate) : buildWeekGrid(referenceDate)),
    [viewMode, referenceDate]
  )
  const from = cells[0]?.date ?? referenceDate
  const to = cells[cells.length - 1]?.date ?? referenceDate

  const calendarQuery = useVenueReservationCalendar(venueId, { from, to, courtId })
  const boardQuery = useVenueReservationBoard(venueId, { date: selectedDate, courtId })

  const selectedDay = calendarQuery.data?.days.find((day) => day.date === selectedDate)
  const courts = courtsQuery.data?.courts.map((court) => ({ courtId: court.courtId, name: court.name })) ?? []
  // 🚨 등록 폼의 코트 선택지는 예약 가능한 코트만 — 삭제·SUSPENDED·MAINTENANCE 코트로 등록하면 서버가
  // 404 를 낸다(#675 §2-2). 위 `courts`(현황판 필터용)와 달리 이 목록은 쓰기 용도라 필터링한다.
  const reservableCourts =
    courtsQuery.data?.courts
      .filter((court) => court.reservable)
      .map((court) => ({ courtId: court.courtId, name: court.name })) ?? []

  function moveReference(delta: number) {
    const next = viewMode === 'MONTH' ? addMonths(referenceDate, delta) : addDays(referenceDate, delta * 7)
    setReferenceDate(next)
    setSelectedDate(next)
  }

  function resetToToday() {
    const today = todayDateKey()
    setReferenceDate(today)
    setSelectedDate(today)
  }

  function switchViewMode(mode: ViewMode) {
    setViewMode(mode)
    setReferenceDate(selectedDate)
  }

  const isClosed = selectedDay?.state === 'CLOSED'
  const isNotOperating = selectedDay?.state === 'NOT_OPERATING'

  return (
    <>
      <PageHeader title="예약 캘린더" description="날짜별·코트별 예약 현황을 확인합니다." />

      {!availability.isLoading && !availability.reservationAvailable ? (
        <Alert tone="warning" className="mb-4">
          <p>예약 기능이 꺼져 있어 예약 등록·변경 기능을 사용할 수 없습니다. 조회는 계속 가능합니다.</p>
          <Link
            to={`/venues/${venueId}/reservation-setting`}
            className="mt-1 inline-block text-brand-500 underline"
          >
            예약 기능 설정으로 이동
          </Link>
        </Alert>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div role="tablist" aria-label="보기 전환" className="flex gap-1 rounded-lg border border-slateish-300 p-1">
            {(['MONTH', 'WEEK'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                role="tab"
                aria-selected={viewMode === mode}
                onClick={() => {
                  switchViewMode(mode)
                }}
                className={`rounded-md px-3 py-1 text-sm font-medium ${
                  viewMode === mode ? 'bg-brand-500 text-white' : 'text-slateish-600'
                }`}
              >
                {mode === 'MONTH' ? '월간' : '주간'}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="sm" onClick={() => moveReference(-1)}>
            이전
          </Button>
          <Button variant="ghost" size="sm" onClick={resetToToday}>
            오늘
          </Button>
          <Button variant="ghost" size="sm" onClick={() => moveReference(1)}>
            다음
          </Button>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-40">
            <label className="sr-only" htmlFor="calendar-court-filter">
              코트 필터
            </label>
            <Select
              id="calendar-court-filter"
              value={courtId}
              onChange={(event) => {
                const value = event.target.value
                setCourtId(value === 'ALL' ? 'ALL' : Number(value))
              }}
            >
              <option value="ALL">전체 코트</option>
              {courts.map((court) => (
                <option key={court.courtId} value={court.courtId}>
                  {court.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col items-start gap-1">
            {/* 🚨 fail-closed — 로딩 중에도 잠근다(구 `PassGuardedButton`과 같은 원칙). 판정 불가
                상태에서 쓰기를 여는 쪽이 위험하고, 어차피 서버가 409 로 거부한다. */}
            <Button
              variant="secondary"
              size="sm"
              disabled={availability.isLoading || !availability.reservationAvailable}
              onClick={() => {
                setManualCreateOpen(true)
              }}
            >
              + 전화·현장 예약 등록
            </Button>
            {!availability.isLoading && !availability.reservationAvailable ? (
              <Link
                to={`/venues/${venueId}/reservation-setting`}
                className="text-xs text-brand-500 underline"
              >
                예약 기능 설정으로 이동
              </Link>
            ) : null}
          </div>
        </div>
      </div>

      <div className="mb-3">
        <ReservationLegend variant="DAY_BUCKET" />
      </div>

      {calendarQuery.isLoading ? <LoadingState rows={4} label="캘린더를 불러오는 중" /> : null}
      {calendarQuery.isError ? (
        <ErrorState
          error={calendarQuery.error}
          onRetry={() => {
            void calendarQuery.refetch()
          }}
        />
      ) : null}

      {calendarQuery.data ? (
        <ReservationCalendarGrid
          cells={cells}
          days={calendarQuery.data.days}
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
        />
      ) : null}

      <div className="mt-6 space-y-3">
        <h3 className="text-base font-semibold text-ink-700">{selectedDate} 코트별 예약 현황</h3>

        {boardQuery.isLoading ? <LoadingState rows={3} label="현황판을 불러오는 중" /> : null}
        {boardQuery.isError ? (
          <ErrorState
            error={boardQuery.error}
            onRetry={() => {
              void boardQuery.refetch()
            }}
          />
        ) : null}

        {boardQuery.data ? (
          isClosed || isNotOperating ? (
            <p className="rounded-xl border border-slateish-200 bg-white px-5 py-8 text-center text-sm text-slateish-600">
              {isClosed ? CLOSED_NOTICE : NOT_OPERATING_NOTICE}
            </p>
          ) : (
            <>
              <ReservationCourtSummaryPanel courts={boardQuery.data.courts} />
              <ReservationLegend variant="BOARD" />
              <ReservationBoardTable
                courts={boardQuery.data.courts}
                onSelectReservation={(reservationId) => {
                  navigate(`/venues/${venueId}/bookings/${reservationId}`)
                }}
              />
              <div className="space-y-1 rounded-lg bg-slateish-50 px-4 py-3 text-xs text-slateish-600">
                <p>
                  임시 점유: 결제 단계에 진입하면 해당 시간이 잠깁니다. 결제가 완료되거나 시간이 초과되면
                  자동으로 해제됩니다.
                </p>
                <p>
                  예약 확인 중: 결제는 완료됐지만 예약 확정 처리가 진행 중입니다. 확정에 실패하면 자동으로
                  취소·환불 후 예약 가능 상태로 전환됩니다.
                </p>
              </div>
            </>
          )
        ) : null}
      </div>

      {manualCreateOpen ? (
        <ReservationManualCreateModal
          venueId={venueId}
          courts={reservableCourts}
          defaultDate={selectedDate}
          onClose={() => {
            setManualCreateOpen(false)
          }}
        />
      ) : null}
    </>
  )
}
